import { useState } from 'react';
import { ArrowLeftIcon, XMarkIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useClientsQuery } from '@/features/Clients/Data';
import { ClientCombobox } from '@/components/shared';
import { useRegrasClienteQuery, useConfigSIMutations } from '../../../hooks/data';
/** Campos booleanos de ConfigRegrasCliente administráveis por essa tela genérica — cada
 * um vira uma entrada no menu de Configurações (ver ConfiguracoesDrawer). */
type CampoBooleano = 'permiteEquipamentoCliente' | 'usaEquipamentoHomologado';

interface RegraClienteBooleanConfigProps {
  campo: CampoBooleano;
  titulo: string;
  descricao: string;
  onVoltar: () => void;
  /** Só faz sentido junto de `permiteEquipamentoCliente` — mostra, por Cliente já
   * selecionado, os campos de nome/CNPJ da empresa proprietária do equipamento (usados no
   * Termo gerado quando o equipamento é do Cliente). Antes era uma tela separada; fundida
   * aqui porque os dois configuram exatamente o mesmo conjunto de Clientes. */
  mostrarEmpresaProprietaria?: boolean;
}

/**
 * Configuração genérica "busca e seleciona um ou mais Clientes" pra um campo booleano de
 * `ConfigRegrasCliente` — reaproveitada por cada categoria do menu de Configurações
 * (Equipamento do Cliente, Equipamento Homologado, e as que entrarem depois) em vez de
 * duplicar a mesma tela pra cada uma.
 */
export default function RegraClienteBooleanConfig({
  campo,
  titulo,
  descricao,
  onVoltar,
  mostrarEmpresaProprietaria,
}: RegraClienteBooleanConfigProps) {
  const { data: clientes = [] } = useClientsQuery();
  const { data: regras = [] } = useRegrasClienteQuery();
  const { atualizarRegrasCliente } = useConfigSIMutations();

  // Muda a cada seleção só pra remontar o ClientCombobox (limpa a busca/seleção interna
  // dele) — permite escolher vários clientes em sequência sem editar o componente
  // compartilhado, que é single-select por padrão (mesmo usado em Solicitações/Alocações).
  const [resetKey, setResetKey] = useState(0);
  const [inputsEmpresa, setInputsEmpresa] = useState<Record<string, { nome: string; cnpj: string }>>({});
  // Clientes cujo card de empresa proprietária está em modo edição (inputs abertos) — os
  // demais mostram os dados já salvos fixos, com Editar/Excluir (em vez de sempre editável,
  // sem nenhuma confirmação visual de que salvou).
  const [editandoEmpresa, setEditandoEmpresa] = useState<Set<string>>(new Set());

  const clientesHabilitados = clientes.filter(
    (cliente) => regras.find((regra) => regra.clienteId === cliente.id)?.[campo] === true
  );

  const handleAdicionar = (clienteId: string) => {
    atualizarRegrasCliente.mutate({ clienteId, [campo]: true });
    setResetKey((atual) => atual + 1);
    // Cliente recém-adicionado ainda não tem nome/CNPJ salvos — abre já em modo edição.
    setEditandoEmpresa((atual) => new Set(atual).add(clienteId));
  };

  const handleRemover = (clienteId: string) => {
    atualizarRegrasCliente.mutate({ clienteId, [campo]: false });
  };

  const valorEmpresa = (clienteId: string, campoEmpresa: 'nome' | 'cnpj'): string => {
    if (inputsEmpresa[clienteId]?.[campoEmpresa] !== undefined) return inputsEmpresa[clienteId][campoEmpresa];
    const regra = regras.find((r) => r.clienteId === clienteId);
    return (campoEmpresa === 'nome' ? regra?.nomeEmpresaProprietaria : regra?.cnpjEmpresaProprietaria) ?? '';
  };

  const handleChangeEmpresa = (clienteId: string, campoEmpresa: 'nome' | 'cnpj', valor: string) => {
    setInputsEmpresa((atual) => ({
      ...atual,
      [clienteId]: {
        nome: campoEmpresa === 'nome' ? valor : valorEmpresa(clienteId, 'nome'),
        cnpj: campoEmpresa === 'cnpj' ? valor : valorEmpresa(clienteId, 'cnpj'),
      },
    }));
  };

  const handleSalvarEmpresa = (clienteId: string) => {
    atualizarRegrasCliente.mutate(
      {
        clienteId,
        nomeEmpresaProprietaria: valorEmpresa(clienteId, 'nome'),
        cnpjEmpresaProprietaria: valorEmpresa(clienteId, 'cnpj'),
      },
      {
        onSuccess: () => {
          setEditandoEmpresa((atual) => {
            const novo = new Set(atual);
            novo.delete(clienteId);
            return novo;
          });
        },
      }
    );
  };

  const handleEditarEmpresa = (clienteId: string) => {
    setEditandoEmpresa((atual) => new Set(atual).add(clienteId));
  };

  return (
    <div>
      <button
        type="button"
        onClick={onVoltar}
        className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800"
      >
        <ArrowLeftIcon className="h-4 w-4" aria-hidden />
        Voltar
      </button>

      <p className="mt-3 font-montserrat font-medium text-gray-900">{titulo}</p>
      <p className="mt-1 text-sm text-gray-600">{descricao}</p>

      <div className="mt-4">
        <ClientCombobox
          key={resetKey}
          selectedId={null}
          placeholder="Buscar cliente..."
          onChange={(cliente) => cliente && handleAdicionar(cliente.id)}
        />
      </div>

      <div className="mt-4 space-y-2">
        {clientesHabilitados.length === 0 && (
          <p className="text-sm text-gray-500">Nenhum cliente selecionado ainda.</p>
        )}
        {!mostrarEmpresaProprietaria &&
          clientesHabilitados.map((cliente) => (
            <span
              key={cliente.id}
              className="mr-2 inline-flex items-center gap-1.5 rounded-full bg-indigo-50 py-1 pl-3 pr-1.5 text-sm text-indigo-700"
            >
              {cliente.name}
              <button
                type="button"
                onClick={() => handleRemover(cliente.id)}
                className="rounded-full p-0.5 text-indigo-500 hover:bg-indigo-100 hover:text-indigo-700"
                title="Remover"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}

        {mostrarEmpresaProprietaria &&
          clientesHabilitados.map((cliente) => {
            const estaEditando = editandoEmpresa.has(cliente.id);
            return (
              <div key={cliente.id} className="rounded-md border border-gray-200 p-3">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-gray-900">{cliente.name}</p>
                  {!estaEditando && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditarEmpresa(cliente.id)}
                        className="btn-icon text-indigo-600 hover:text-indigo-900"
                        title="Editar"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemover(cliente.id)}
                        className="btn-icon text-red-600 hover:text-red-900"
                        title="Excluir"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs font-medium uppercase text-gray-400">Empresa proprietária do equipamento</p>

                {estaEditando ? (
                  <div className="mt-1 space-y-2">
                    <input
                      type="text"
                      className="input-primary w-full"
                      placeholder="Nome da empresa (ex: Banco PINE S/A)"
                      value={valorEmpresa(cliente.id, 'nome')}
                      onChange={(event) => handleChangeEmpresa(cliente.id, 'nome', event.target.value)}
                    />
                    <input
                      type="text"
                      className="input-primary w-full"
                      placeholder="CNPJ (ex: 62.144.175/0001-20)"
                      value={valorEmpresa(cliente.id, 'cnpj')}
                      onChange={(event) => handleChangeEmpresa(cliente.id, 'cnpj', event.target.value)}
                    />
                    <button
                      type="button"
                      className="btn-secondary text-sm"
                      onClick={() => handleSalvarEmpresa(cliente.id)}
                      disabled={atualizarRegrasCliente.isPending}
                    >
                      Salvar
                    </button>
                  </div>
                ) : (
                  <div className="mt-1 space-y-1">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium text-gray-500">Nome:</span> {valorEmpresa(cliente.id, 'nome') || '—'}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium text-gray-500">CNPJ:</span> {valorEmpresa(cliente.id, 'cnpj') || '—'}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
